import { useState } from 'react';
import type { PendingSteps } from '../lib/pending-steps';
import { usePendingSteps } from '../lib/use-pending-steps';

/**
 * Renders outstanding operator checklists at SHELL level, on every
 * pane, until explicitly acknowledged.
 *
 * Placement is the point. A checklist shown only on the pane that
 * produced it is a checklist you stop seeing the moment you navigate
 * away -- and the decommission case, where not running the steps
 * leaves a retired certificate with live bus write access, is exactly
 * the case where you navigate away (to verify the site is gone).
 *
 * See `lib/pending-steps.ts` for why this is persisted rather than
 * held in React state.
 */

function StepList({ entry, onAcknowledge }: { entry: PendingSteps; onAcknowledge: () => void }) {
  const isDecommission = entry.action === 'decommission';
  const [copied, setCopied] = useState(false);

  const copy = () => {
    void navigator.clipboard?.writeText(entry.steps.join('\n')).then(
      () => {
        setCopied(true);
        window.setTimeout(() => setCopied(false), 2000);
      },
      () => setCopied(false),
    );
  };

  return (
    <div
      role="alert"
      style={{
        border: `1px solid ${isDecommission ? 'var(--nx-error)' : 'var(--nx-warning)'}`,
        borderRadius: 'var(--nx-r)',
        background: 'var(--nx-bg3)',
        padding: '12px 16px',
        marginBottom: 10,
      }}
    >
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 10, flexWrap: 'wrap' }}>
        <strong style={{ color: isDecommission ? 'var(--nx-error)' : 'var(--nx-warning)' }}>
          {isDecommission ? 'Bus access NOT yet revoked' : 'Site not yet commissioned'}
        </strong>
        <code>{entry.site_id}</code>
        <span style={{ fontSize: 12, color: 'var(--nx-muted)' }}>
          {new Date(entry.at_ms).toLocaleString()}
        </span>
      </div>
      <p style={{ margin: '6px 0', fontSize: 13, color: 'var(--nx-muted)' }}>
        {isDecommission
          ? 'The site is archived and its keyspace purged, but until the ACL is regenerated and the bus restarted, its certificate still has write access to the platform bus. Archiving hides a site; it revokes nothing.'
          : 'The database record exists. The site will not appear on the bus until these host steps are run — this API holds neither docker.sock nor the CA key, by design.'}
      </p>
      <ol style={{ margin: '8px 0', paddingLeft: 22 }}>
        {entry.steps.map((step, i) => (
          <li key={i} style={{ marginBottom: 4 }}>
            <code style={{ fontSize: 12, whiteSpace: 'pre-wrap' }}>{step}</code>
          </li>
        ))}
      </ol>
      <div style={{ display: 'flex', gap: 8 }}>
        <button type="button" onClick={copy}>
          {copied ? 'Copied' : 'Copy commands'}
        </button>
        <button type="button" onClick={onAcknowledge}>
          I have run these
        </button>
      </div>
      <p style={{ margin: '6px 0 0', fontSize: 12, color: 'var(--nx-muted)' }}>
        Dismissing only clears this reminder. Whether the steps actually ran is answered by the
        site’s state in the Sites table, which observes the bus.
      </p>
    </div>
  );
}

export function PendingStepsBanner() {
  const [entries, acknowledge] = usePendingSteps();
  if (entries.length === 0) return null;
  return (
    <div style={{ padding: '12px 20px 0' }}>
      {entries.map((entry) => (
        <StepList
          key={`${entry.site_id}:${entry.action}`}
          entry={entry}
          onAcknowledge={() => acknowledge(entry.site_id, entry.action)}
        />
      ))}
    </div>
  );
}
